import { Frequency } from '@vendasta/galaxy/frequency';
import { ItemFeeRow, ItemRow, PriceSummary } from './item-pricing-table.interface';
import { ItemFrequencyPrice } from '../common/item-price';
import { AppPrices, FeeAmountType, FrequencyPrice } from '@vendasta/marketplace-apps';
import { getFrequencyPrices } from '../common/item-price';
import { toGalaxyFrequency } from '@vendasta/billing-ui';
import { InventoryItemWithPricing } from '../common/inventory-item';

export function parseItemFees(appPrices: AppPrices, item: InventoryItemWithPricing): ItemFeeRow[] {
  const editionId = item.type === 'app' ? item.edition?.editionId : undefined;
  const retailFrequencyPrices = getFrequencyPrices(appPrices, editionId, 'retail');
  const wholesaleFrequencyPrices = getFrequencyPrices(appPrices, editionId, 'wholesale');

  const fees: ItemFeeRow[] = [];
  const setupFee = parseSetupFee(wholesaleFrequencyPrices, retailFrequencyPrices, item);
  if (setupFee) {
    fees.push(setupFee);
  }

  const managementFees = parseManagementFees(wholesaleFrequencyPrices, item);
  fees.push(...managementFees);

  return fees;
}

export function convertHTMLControlValueToNumber(value: string | number): number {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/,/g, ''));
  }
  return value;
}

export function calculateTotalWholesalePrice(items: ItemRow[]): PriceSummary {
  const priceSummary: PriceSummary = { oneTime: 0, monthly: 0, yearly: 0, currency: '' };

  items.forEach((item) => {
    if (!priceSummary.currency && item.wholesalePricing.currency) {
      priceSummary.currency = item.wholesalePricing.currency;
    }

    item.wholesalePricing.frequencyPrices?.forEach((price) => {
      addPriceBasedOnFrequency(price, priceSummary);
      addFeesBasedOnFrequency(item, 'wholesale', priceSummary);
    });
  });

  return priceSummary;
}

export function calculateTotalRetailPrice(items: ItemRow[]): PriceSummary {
  const priceSummary: PriceSummary = { oneTime: 0, monthly: 0, yearly: 0, currency: '' };

  items.forEach((item) => {
    if (!priceSummary.currency && item.retailPricing.currency) {
      priceSummary.currency = item.retailPricing.currency;
    }

    addPriceBasedOnFrequency(item.retailPricing, priceSummary);
    addFeesBasedOnFrequency(item, 'retail', priceSummary);
  });

  return priceSummary;
}

function addPriceBasedOnFrequency(itemPrice: ItemFrequencyPrice, priceSummary: PriceSummary) {
  if (!itemPrice.price) return;

  // one time total needs to be updated regardless of the type of frequency
  priceSummary.oneTime += itemPrice.total;
  if (itemPrice.frequency === Frequency.MONTHLY) {
    priceSummary.monthly += itemPrice.total;
  }
  if (itemPrice.frequency === Frequency.YEARLY) {
    priceSummary.yearly += itemPrice.total;
  }
}

function addFeesBasedOnFrequency(item: ItemRow, priceType: 'wholesale' | 'retail', priceSummary: PriceSummary) {
  item.fees.forEach((fee) => {
    if (priceType === 'wholesale') {
      const wholesaleTotal = fee.wholesaleAmount * item.quantity;
      priceSummary.oneTime += wholesaleTotal;
      if (fee.type === 'management' && fee.frequency === Frequency.MONTHLY) {
        priceSummary.monthly += wholesaleTotal;
      }
      if (fee.type === 'management' && fee.frequency === Frequency.YEARLY) {
        priceSummary.yearly += wholesaleTotal;
      }
    }

    if (priceType === 'retail') {
      priceSummary.oneTime += fee.retailAmountTotal ?? 0;
    }
  });
}

export function updatePercentageManagementFee(item: ItemRow, oldWholeSalePrice: number): void {
  item.fees.forEach((fee) => {
    if (fee.type === 'management' && fee.amountType === 'percentage') {
      const managementFeePercentage = Math.round((fee.wholesaleAmount / oldWholeSalePrice) * 100);
      const frequencyPrice = item.wholesalePricing.frequencyPrices.find((p) => p.frequency === fee.frequency);
      const wholesalePrice = frequencyPrice?.price || 0;
      fee.wholesaleAmount = calculatePercentageFee(managementFeePercentage, wholesalePrice);
    }
  });
}

export function calculatePercentageFee(percentage: number, price: number) {
  return Math.round((percentage * price) / 100);
}

function parseSetupFee(
  wholesalePrices: FrequencyPrice[],
  retailPrices: FrequencyPrice[],
  item: InventoryItemWithPricing,
): ItemFeeRow | null {
  const wholesaleSetupFeeTotal = wholesalePrices.reduce((acc, price) => acc + (price.setupFee || 0), 0);
  const retailSetupFeeTotal = parseRetailSetupFee(retailPrices, item);

  if (!wholesaleSetupFeeTotal && !retailSetupFeeTotal) return null;

  return {
    type: 'setup',
    label: 'Setup fee',
    amountType: 'fixed',
    wholesaleAmount: wholesaleSetupFeeTotal,
    retailAmount: retailSetupFeeTotal,
    retailAmountTotal: retailSetupFeeTotal * item.quantity,
    frequency: Frequency.ONE_TIME,
  };
}

function parseRetailSetupFee(retailPrices: FrequencyPrice[], item: InventoryItemWithPricing): number {
  const revenueComponents = item.pricing?.currentRevenue?.revenueComponents;

  // default to marketplaceplace-apps fees if no revenue components are present
  if (!revenueComponents) return retailPrices.reduce((acc, price) => acc + (price.setupFee || 0), 0);

  let retailSetupFeeTotal = 0;
  revenueComponents.forEach((rc, i) => {
    // one time is the default enum value
    const isOneTime = !rc.period;

    // skip the first revenue component since it represents the base price
    if (i > 0 && isOneTime) {
      retailSetupFeeTotal += rc.value ?? 0;
    }
  });
  return retailSetupFeeTotal;
}

function parseManagementFees(wholesaleFrequencyPrices: FrequencyPrice[], item: InventoryItemWithPricing): ItemFeeRow[] {
  const managementFees: ItemFeeRow[] = [];

  wholesaleFrequencyPrices.forEach((price) => {
    // exclude management fees when discounted wholesale price is zero
    if (price.instancePrice && getDiscountedBasePrice(price) <= 0) {
      return;
    }

    price.fees?.forEach((fee) => {
      if (fee.amountType === FeeAmountType.FEE_AMOUNT_TYPE_FIXED) {
        managementFees.push({
          type: 'management',
          label: 'Management fee',
          amountType: 'fixed',
          wholesaleAmount: fee.amount,
          frequency: toGalaxyFrequency(price.frequency),
        });
      }

      if (fee.amountType === FeeAmountType.FEE_AMOUNT_TYPE_PERCENTAGE) {
        let wholesalePrice = price.pricingRules?.[0]?.price;
        // use custom price if it is present
        if (item.pricing?.cost?.customPrice) {
          wholesalePrice = item.pricing.cost.customPrice;
        }
        managementFees.push({
          type: 'management',
          label: `Management fee (${fee.amount}%)`,
          amountType: 'percentage',
          wholesaleAmount: (fee.amount * wholesalePrice) / 100,
          frequency: toGalaxyFrequency(price.frequency),
        });
      }
    });
  });

  return managementFees;
}

function getDiscountedBasePrice(price: FrequencyPrice): number {
  const instancePrice = price.instancePrice?.total ?? 0;
  const setupFee = price.setupFee ?? 0;

  return instancePrice - setupFee;
}
